import { useState } from 'react';
import { useUsers } from '../../api/admin';
import { useCreateFreeTrial, useTrialBundles } from '../../api/funnel';
import { Modal } from '../../components/Modal';
import { useDebounced } from '../../hooks/useDebounced';
import { ApiError } from '../../lib/api';
import { fromLocalInput } from '../../lib/format';
import type { AdminTrial } from '../../types/api';
import { TutorPicker } from '../../components/TutorPicker';

export function CreateFreeTrialModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (trial: AdminTrial) => void;
}) {
  const create = useCreateFreeTrial();
  const bundles = useTrialBundles();
  const [search, setSearch] = useState('');
  const query = useDebounced(search);
  const students = useUsers({ role: 'student', isActive: true, q: query.trim() || undefined, limit: 10 });
  const [student, setStudent] = useState<{ id: string; name: string }>();
  const [bundleId, setBundleId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [tutorId, setTutorId] = useState<string>();
  const [start, setStart] = useState('');
  const [duration, setDuration] = useState(30);
  const [reason, setReason] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const bundle = bundles.data?.find((item) => item.id === bundleId);
  const subject = bundle?.subjects.find((item) => item.id === subjectId);
  const startTime = fromLocalInput(start);
  const valid = student && bundle && subject && tutorId && startTime && Date.parse(startTime) > Date.now()
    && Number.isInteger(duration) && duration >= 15 && duration <= 240 && reason.trim();
  const changed = () => { setIdempotencyKey(crypto.randomUUID()); create.reset(); };
  const submit = () => {
    if (!valid || !student || !tutorId || !startTime) return;
    create.mutate({ studentId: student.id, tutorId, bundlePackageId: bundleId, subjectId,
      startTime, durationMinutes: duration, reason: reason.trim(), idempotencyKey }, { onSuccess: onCreated });
  };
  return (
    <Modal open title="Add free trial" width="lg" onClose={onClose}
      description="Book one introductory lesson for a student and tutor. No payment or subscription credits are needed."
      footer={<>
        <button type="button" className="btn btn-secondary" disabled={create.isPending} onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!valid || create.isPending} onClick={submit}>
          {create.isPending ? 'Booking…' : 'Book free trial'}
        </button>
      </>}>
      <div className="max-h-[60vh] space-y-4 overflow-y-auto">
      <fieldset disabled={create.isPending} className="space-y-4">
        <div>
          {student ? <div className="flex items-center justify-between gap-2 rounded border border-rule p-3">
            <p className="text-sm">Student: {student.name}</p>
            <button type="button" className="btn btn-secondary" onClick={() => { setStudent(undefined); changed(); }}>Change student</button>
          </div> : <>
            <label className="block text-sm">Find student
              <input className="input mt-1" type="search" value={search} placeholder="Search by name or email"
                onChange={(event) => setSearch(event.target.value)} />
            </label>
            {students.isFetching && <p role="status" className="text-sm">Finding students…</p>}
            {students.isError && <p role="alert" className="text-sm text-danger">Could not load students. <button type="button" onClick={() => void students.refetch()}>Try again</button></p>}
            {!students.isFetching && students.data?.data.length === 0 && <p className="text-sm">No active students match. Try another name or email.</p>}
            <fieldset className="mt-2 max-h-40 overflow-y-auto rounded border border-rule" aria-label="Student results">
              {!students.isFetching && students.data?.data.map((item) => <button type="button" key={item.id}
                className="block w-full border-b border-rule p-2 text-start text-sm last:border-0 hover:bg-surface-2"
                onClick={() => { setStudent({ id: item.id, name: item.fullName }); changed(); }}>
                <span className="block">{item.fullName}</span><span className="block break-all text-xs text-ink-3">{item.email}</span>
              </button>)}
            </fieldset>
          </>}
        </div>
        <label className="block text-sm">Related bundle
          <select className="input mt-1" value={bundleId} disabled={bundles.isPending || bundles.isError}
            onChange={(event) => { setBundleId(event.target.value); setSubjectId(''); setTutorId(undefined); changed(); }}>
            <option value="">Choose a bundle</option>
            {bundles.data?.map((item) => <option key={item.id} value={item.id}>{item.nameAr || item.nameEn}</option>)}
          </select>
          <span className="mt-1 block text-xs text-ink-3">The trial introduces this bundle. It does not buy a subscription.</span>
        </label>
        {bundles.isPending && <p role="status" className="text-sm">Loading bundles…</p>}
        {bundles.isError && <p role="alert" className="text-sm text-danger">Could not load bundles. <button type="button" onClick={() => void bundles.refetch()}>Try again</button></p>}
        {bundles.isSuccess && bundles.data.length === 0 && <p className="text-sm">No active bundles are available for a trial.</p>}
        <label className="block text-sm">Trial subject
          <select className="input mt-1" value={subjectId} disabled={!bundle}
            onChange={(event) => { setSubjectId(event.target.value); setTutorId(undefined); changed(); }}>
            <option value="">Choose a subject</option>
            {bundle?.subjects.map((item) => <option key={item.id} value={item.id}>{item.nameAr || item.nameEn}</option>)}
          </select>
        </label>
        {subject && <TutorPicker label="Trial tutor" value={tutorId} subjectSlug={subject.slug}
          onChange={(id) => { setTutorId(id); changed(); }} />}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">Lesson starts
            <input className="input mt-1" type="datetime-local" value={start}
              onChange={(event) => { setStart(event.target.value); changed(); }} />
            <span className="mt-1 block text-xs text-ink-3">Your timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
          </label>
          <label className="block text-sm">Duration (minutes)
            <input className="input mt-1" type="number" min={15} max={240} step={1} value={duration}
              onChange={(event) => { setDuration(Number(event.target.value)); changed(); }} />
          </label>
        </div>
        <label className="block text-sm">Reason for free trial
          <textarea className="input mt-1" value={reason} maxLength={500}
            onChange={(event) => { setReason(event.target.value); changed(); }} />
          <span className="mt-1 block text-xs text-ink-3">Private admin note for the audit history.</span>
        </label>
        <p className="rounded border border-rule bg-surface-2 p-3 text-sm">
          Student fee: 0. This free lesson generates no tutor earnings. Both participants receive a booking notification.
          The student's existing introductory trial eligibility still applies.
        </p>
      </fieldset>
      {create.isError && <p role="alert" className="mt-3 text-sm text-danger">
        {create.error instanceof ApiError ? create.error.message : 'Could not book the free trial. Review the details and try again.'}
      </p>}
      </div>
    </Modal>
  );
}
